import { FormEvent, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  clearPartnerToken,
  getPartnerToken,
  partnerApi,
  setPartnerToken,
} from '../../lib/partnerApi'
import { getApiError } from '../../lib/api'
import { buildReferralInviteUrl } from '../../services/referral'
import { toBcp47 } from '../../hooks/useLocalizedFormat'
import { formatMoney } from '../../lib/formatMoney'

interface PartnerMe {
  partner: {
    email: string
    first_name: string
    last_name: string
    bank_account_number: string | null
    bank_account_display: string | null
  }
  code: string | null
  reward?: {
    points_for_client: number
    points_validity_days: number
    commission_grosz: number
  }
  stats: { registrations: number; checkouts: number; purchases: number }
  pending_registrations: Array<{ id: number; referee_email: string; created_at: string | null }>
  conversions: Array<{
    id: number
    referee_email: string | null
    product_kind: string | null
    tier_code: string | null
    purchase_amount: number | null
    purchase_amount_net: number | null
    reward_status: string
    created_at: string | null
  }>
  commissions: Array<{
    id: number
    amount_grosz: number
    status: string
    referee_email: string | null
    tier_code: string | null
    purchase_amount_net: number | null
    created_at: string | null
    paid_at: string | null
  }>
  commission_summary: {
    pending_grosz: number
    paid_grosz: number
    total_grosz: number
    requested_grosz?: number
  }
  conversion_summary?: { count: number; purchase_net_grosz: number }
  payout?: {
    can_request: boolean
    blocked_reason: string | null
    available_grosz: number
    requested_grosz: number
    open_request: PayoutRequestRow | null
    processing_hint: string
  }
  payout_requests?: PayoutRequestRow[]
}

interface PayoutRequestRow {
  id: number
  status: string
  amount_grosz: number
  commission_count?: number
  bank_account_display: string | null
  bank_account_number: string | null
  requested_at: string | null
  paid_at: string | null
}

function formatPln(grosz: number, lang: string) {
  return formatMoney(grosz, 'PLN', toBcp47(lang))
}

function translateOrRaw(t: (key: string) => string, prefix: string, value: string | null) {
  if (!value) return '—'
  const key = `${prefix}.${value}`
  const translated = t(key)
  return translated === key ? value : translated
}

export default function PartnerDashboardPage() {
  const { t, i18n } = useTranslation('partners')
  const [email, setEmail] = useState('')
  const [otp, setOtp] = useState('')
  const [otpSent, setOtpSent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [copied, setCopied] = useState<'link' | 'code' | null>(null)
  const [me, setMe] = useState<PartnerMe | null>(null)
  const [bankAccount, setBankAccount] = useState('')
  const [bankSaving, setBankSaving] = useState(false)
  const [bankMessage, setBankMessage] = useState<string | null>(null)
  const [bankError, setBankError] = useState<string | null>(null)
  const [payoutLoading, setPayoutLoading] = useState(false)
  const [payoutError, setPayoutError] = useState<string | null>(null)
  const [payoutMessage, setPayoutMessage] = useState<string | null>(null)

  const loadMe = async () => {
    const res = await partnerApi.get('/partners/me')
    setMe(res.data)
    setBankAccount(
      res.data.partner.bank_account_display ?? res.data.partner.bank_account_number ?? '',
    )
  }

  useEffect(() => {
    if (!getPartnerToken()) return
    loadMe().catch(() => {
      clearPartnerToken()
      setMe(null)
    })
  }, [])

  const requestCode = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setLoading(true)
    try {
      await partnerApi.post('/partners/auth/request-code', { email })
      setOtpSent(true)
    } catch (err) {
      setError(getApiError(err, t('login.requestFailed')))
    } finally {
      setLoading(false)
    }
  }

  const verifyCode = async (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await partnerApi.post('/partners/auth/verify-code', { email, code: otp })
      setPartnerToken(res.data.access_token)
      await loadMe()
    } catch (err) {
      setError(getApiError(err, t('login.verifyFailed')))
    } finally {
      setLoading(false)
    }
  }

  const copyInviteLink = async () => {
    if (!me?.code) return
    try {
      await navigator.clipboard.writeText(buildReferralInviteUrl(me.code))
      setCopied('link')
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }

  const copyCode = async () => {
    if (!me?.code) return
    try {
      await navigator.clipboard.writeText(me.code)
      setCopied('code')
      setTimeout(() => setCopied(null), 1500)
    } catch {
      setCopied(null)
    }
  }

  const saveBankAccount = async (event: FormEvent) => {
    event.preventDefault()
    setBankError(null)
    setBankMessage(null)
    setBankSaving(true)
    try {
      const res = await partnerApi.patch('/partners/me/bank-account', {
        bank_account_number: bankAccount,
      })
      setBankAccount(res.data.bank_account_display ?? res.data.bank_account_number ?? '')
      setBankMessage(t('dashboard.bankAccount.saved'))
      await loadMe()
    } catch (err) {
      setBankError(getApiError(err, t('dashboard.bankAccount.saveFailed')))
    } finally {
      setBankSaving(false)
    }
  }

  const requestPayout = async () => {
    setPayoutError(null)
    setPayoutMessage(null)
    setPayoutLoading(true)
    try {
      await partnerApi.post('/partners/me/payout-requests')
      setPayoutMessage(t('dashboard.payout.requested'))
      await loadMe()
    } catch (err) {
      setPayoutError(getApiError(err, t('dashboard.payout.requestFailed')))
    } finally {
      setPayoutLoading(false)
    }
  }

  if (!me) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-md mx-auto bg-white rounded-2xl border border-gray-200 p-8">
          <h1 className="text-2xl font-bold text-gray-900">{t('login.title')}</h1>
          <p className="mt-2 text-sm text-gray-500">{t('login.subtitle')}</p>
          <form onSubmit={otpSent ? verifyCode : requestCode} className="mt-8 space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              {t('login.email')}
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                required
                disabled={otpSent}
              />
            </label>
            {otpSent && (
              <label className="block text-sm font-medium text-gray-700">
                {t('login.code')}
                <input
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 tracking-widest"
                  required
                  maxLength={6}
                />
              </label>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              className="w-full rounded-lg bg-[#FF5A00] text-white font-semibold py-2.5 hover:bg-[#e04e00] disabled:opacity-50"
            >
              {loading
                ? t('login.submitting')
                : otpSent
                  ? t('login.verify')
                  : t('login.sendCode')}
            </button>
            <p className="text-center text-sm text-gray-500">
              {t('login.noAccount')}{' '}
              <Link to="/partners" className="text-[#FF5A00] font-medium hover:underline">
                {t('login.signupLink')}
              </Link>
            </p>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-4">
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {me.partner.first_name} {me.partner.last_name} · {me.partner.email}
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              clearPartnerToken()
              setMe(null)
            }}
            className="text-sm text-gray-500 hover:text-gray-800"
          >
            {t('dashboard.logout')}
          </button>
        </div>

        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.yourInvite')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('dashboard.inviteHint')}</p>
          {me.code && (
            <div className="mt-4 space-y-4">
              <div className="rounded-xl border border-orange-100 bg-orange-50/70 px-4 py-3 text-sm text-gray-800 space-y-1.5">
                <p className="font-semibold text-gray-900">{t('dashboard.howItWorks.title')}</p>
                <p>
                  {t('dashboard.howItWorks.clientBonus', {
                    points: me.reward?.points_for_client ?? 500,
                    days: me.reward?.points_validity_days ?? 30,
                  })}
                </p>
                <p>
                  {t('dashboard.howItWorks.yourCommission', {
                    amount: formatPln(me.reward?.commission_grosz ?? 10000, i18n.language),
                  })}
                </p>
                <p className="text-gray-600">{t('dashboard.howItWorks.trigger')}</p>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  {t('dashboard.inviteLink')}
                </p>
                <div className="flex flex-col sm:flex-row gap-2">
                  <code className="flex-1 break-all rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-900">
                    {buildReferralInviteUrl(me.code)}
                  </code>
                  <button
                    type="button"
                    onClick={copyInviteLink}
                    className="rounded-lg bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white hover:bg-[#e04e00] shrink-0"
                  >
                    {copied === 'link' ? t('dashboard.copied') : t('dashboard.copyLink')}
                  </button>
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
                  {t('dashboard.yourCode')}
                </p>
                <div className="flex items-center gap-3">
                  <span className="font-mono text-2xl font-bold tracking-wide">{me.code}</span>
                  <button
                    type="button"
                    onClick={copyCode}
                    className="rounded-lg border border-gray-300 px-3 py-1 text-sm font-semibold hover:bg-gray-50"
                  >
                    {copied === 'code' ? t('dashboard.copied') : t('dashboard.copyCode')}
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-500">{t('dashboard.codeHint')}</p>
              </div>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.bankAccount.title')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('dashboard.bankAccount.subtitle')}</p>
          <form onSubmit={saveBankAccount} className="mt-4 space-y-3 max-w-lg">
            <label className="block text-sm font-medium text-gray-700">
              {t('dashboard.bankAccount.label')}
              <input
                value={bankAccount}
                onChange={(e) => setBankAccount(e.target.value)}
                placeholder={t('dashboard.bankAccount.placeholder')}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 font-mono tracking-wide"
                autoComplete="off"
              />
            </label>
            <p className="text-xs text-gray-500">{t('dashboard.bankAccount.hint')}</p>
            {bankError && <p className="text-sm text-red-600">{bankError}</p>}
            {bankMessage && <p className="text-sm text-green-600">{bankMessage}</p>}
            <button
              type="submit"
              disabled={bankSaving}
              className="rounded-lg bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
            >
              {bankSaving ? t('dashboard.bankAccount.saving') : t('dashboard.bankAccount.save')}
            </button>
          </form>
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label={t('dashboard.stats.registrations')} value={me.stats.registrations} />
          <StatCard label={t('dashboard.stats.checkouts')} value={me.stats.checkouts} />
          <StatCard label={t('dashboard.stats.purchases')} value={me.stats.purchases} />
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label={t('dashboard.earnings.pending')} value={formatPln(me.commission_summary.pending_grosz, i18n.language)} />
          <StatCard
            label={t('dashboard.earnings.requested')}
            value={formatPln(me.commission_summary.requested_grosz ?? me.payout?.requested_grosz ?? 0, i18n.language)}
          />
          <StatCard label={t('dashboard.earnings.paid')} value={formatPln(me.commission_summary.paid_grosz, i18n.language)} />
          <StatCard label={t('dashboard.earnings.total')} value={formatPln(me.commission_summary.total_grosz, i18n.language)} />
        </section>

        <section className="bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.payout.title')}</h2>
            <p className="mt-1 text-sm text-gray-500">{t('dashboard.payout.hint')}</p>
          </div>

          {me.payout?.open_request ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <p className="font-semibold">{t('dashboard.payout.openTitle')}</p>
              <p className="mt-1">
                {t('dashboard.payout.openBody', {
                  amount: formatPln(me.payout.open_request.amount_grosz, i18n.language),
                })}
              </p>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              <p className="text-sm text-gray-700">
                {t('dashboard.payout.available', {
                  amount: formatPln(me.payout?.available_grosz ?? me.commission_summary.pending_grosz, i18n.language),
                })}
              </p>
              <button
                type="button"
                onClick={requestPayout}
                disabled={payoutLoading || !(me.payout?.can_request ?? false)}
                className="rounded-lg bg-[#FF5A00] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
              >
                {payoutLoading ? t('dashboard.payout.requesting') : t('dashboard.payout.request')}
              </button>
            </div>
          )}

          {!me.partner.bank_account_number && (
            <p className="text-sm text-amber-700">{t('dashboard.payout.needBank')}</p>
          )}
          {payoutError && <p className="text-sm text-red-600">{payoutError}</p>}
          {payoutMessage && <p className="text-sm text-green-600">{payoutMessage}</p>}

          {(me.payout_requests ?? []).length > 0 && (
            <div className="overflow-x-auto border-t border-gray-100 pt-4">
              <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('dashboard.payout.historyTitle')}</h3>
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2">{t('dashboard.columns.date')}</th>
                    <th className="px-4 py-2 text-right">{t('dashboard.columns.amount')}</th>
                    <th className="px-4 py-2">{t('dashboard.columns.bank')}</th>
                    <th className="px-4 py-2">{t('dashboard.columns.status')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(me.payout_requests ?? []).map((row) => (
                    <tr key={row.id}>
                      <td className="px-4 py-2 text-gray-500">
                        {row.requested_at ? new Date(row.requested_at).toLocaleString(toBcp47(i18n.language)) : '—'}
                      </td>
                      <td className="px-4 py-2 text-right font-semibold">{formatPln(row.amount_grosz, i18n.language)}</td>
                      <td className="px-4 py-2 font-mono text-xs">
                        {row.bank_account_display ?? row.bank_account_number ?? '—'}
                      </td>
                      <td className="px-4 py-2">
                        {translateOrRaw(t, 'dashboard.payoutStatus', row.status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="grid grid-cols-1 sm:grid-cols-1 gap-4">
          <StatCard
            label={t('dashboard.earnings.purchaseNet')}
            value={formatPln(me.conversion_summary?.purchase_net_grosz ?? 0, i18n.language)}
          />
        </section>

        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.pendingTitle')}</h2>
            <p className="text-sm text-gray-500">{t('dashboard.pendingSubtitle')}</p>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-3">{t('dashboard.columns.date')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.email')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {me.pending_registrations.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-6 py-8 text-center text-gray-400">
                    {t('dashboard.noPending')}
                  </td>
                </tr>
              ) : (
                me.pending_registrations.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-3 text-gray-500">
                      {row.created_at ? new Date(row.created_at).toLocaleString(toBcp47(i18n.language)) : '—'}
                    </td>
                    <td className="px-6 py-3 font-mono">{row.referee_email}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>

        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.conversionsTitle')}</h2>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-3">{t('dashboard.columns.date')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.email')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.kind')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.plan')}</th>
                <th className="px-6 py-3 text-right">{t('dashboard.columns.net')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {!(me.conversions ?? []).length ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                    {t('dashboard.noConversions')}
                  </td>
                </tr>
              ) : (
                (me.conversions ?? []).map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-3 text-gray-500">
                      {row.created_at ? new Date(row.created_at).toLocaleString(toBcp47(i18n.language)) : '—'}
                    </td>
                    <td className="px-6 py-3 font-mono">{row.referee_email ?? '—'}</td>
                    <td className="px-6 py-3">{translateOrRaw(t, 'dashboard.kind', row.product_kind)}</td>
                    <td className="px-6 py-3">{row.tier_code ?? '—'}</td>
                    <td className="px-6 py-3 text-right">
                      {row.purchase_amount_net != null ? formatPln(row.purchase_amount_net, i18n.language) : '—'}
                    </td>
                    <td className="px-6 py-3">{translateOrRaw(t, 'dashboard.status', row.reward_status)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {(me.conversions ?? []).length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-6 py-3" colSpan={4}>{t('dashboard.total')}</td>
                  <td className="px-6 py-3 text-right">
                    {formatPln(me.conversion_summary?.purchase_net_grosz ?? 0, i18n.language)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </section>

        <section className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('dashboard.commissionsTitle')}</h2>
          </div>
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-6 py-3">{t('dashboard.columns.date')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.email')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.plan')}</th>
                <th className="px-6 py-3 text-right">{t('dashboard.columns.net')}</th>
                <th className="px-6 py-3 text-right">{t('dashboard.columns.amount')}</th>
                <th className="px-6 py-3">{t('dashboard.columns.status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {me.commissions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-8 text-center text-gray-400">
                    {t('dashboard.noCommissions')}
                  </td>
                </tr>
              ) : (
                me.commissions.map((row) => (
                  <tr key={row.id}>
                    <td className="px-6 py-3 text-gray-500">
                      {row.created_at ? new Date(row.created_at).toLocaleString(toBcp47(i18n.language)) : '—'}
                    </td>
                    <td className="px-6 py-3 font-mono">{row.referee_email ?? '—'}</td>
                    <td className="px-6 py-3">{row.tier_code ?? '—'}</td>
                    <td className="px-6 py-3 text-right">
                      {row.purchase_amount_net != null ? formatPln(row.purchase_amount_net, i18n.language) : '—'}
                    </td>
                    <td className="px-6 py-3 text-right font-semibold">{formatPln(row.amount_grosz, i18n.language)}</td>
                    <td className="px-6 py-3">{translateOrRaw(t, 'dashboard.commissionStatus', row.status)}</td>
                  </tr>
                ))
              )}
            </tbody>
            {me.commissions.length > 0 && (
              <tfoot>
                <tr className="bg-gray-50 font-semibold">
                  <td className="px-6 py-3" colSpan={3}>{t('dashboard.total')}</td>
                  <td className="px-6 py-3 text-right">
                    {formatPln(me.commissions.reduce((sum, row) => sum + (row.purchase_amount_net ?? 0), 0), i18n.language)}
                  </td>
                  <td className="px-6 py-3 text-right">{formatPln(me.commission_summary.total_grosz, i18n.language)}</td>
                  <td />
                </tr>
              </tfoot>
            )}
          </table>
        </section>
      </div>
    </div>
  )
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <p className="text-sm text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
    </div>
  )
}
